import { css } from '@emotion/css';
import { Button as MantineButton, Tooltip, useMantineTheme } from '@mantine/core';

/**
 * A styled button wrapper around Mantine's Button, with optional tooltip support.
 *
 * @param {object} props
 * @param {React.ReactNode} props.children - Button label or content.
 * @param {() => void} [props.onClick] - Click handler for the button.
 * @param {boolean} [props.disabled=false] - Whether the button is disabled.
 * @param {string|null} [props.disabledReason=null] - Tooltip text explaining why button is disabled.
 * @param {React.ReactNode} [props.icon=null] - Optional icon rendered before the label.
 * @param {boolean} [props.fullWidth=false] - Expand button to full container width.
 * @param {string} [props.additionalClasses=''] - Additional CSS classes to apply.
 * @param {'xs'|'sm'|'md'|'lg'} [props.size='md'] - Mantine button size (also maps to custom font size).
 * @param {'button'|'submit'|'reset'} [props.type='submit'] - Button type attribute.
 * @param {string} [props.color='rose-rush'] - Theme color key from Mantine theme.
 * @param {'filled'|'outline'|'light'|'default'|'subtle'|'unstyled'} [props.variant='filled'] - Visual button variant.
 * @param {number} [props.variantLightIndex=6] - Color index for light variant styling.
 * @param {number} [props.variantDarkIndex=8] - Color index for dark variant styling.
 * @param {string|null} [props.tooltip=null] - Tooltip text to display on hover.
 * @param {object} [props.tooltipProps={}] - Props forwarded to Mantine Tooltip.
 * @param {object} [props.props] - Additional props forwarded to Mantine Button.
 * @returns {JSX.Element}
 */
const Button = ({
  children,
  onClick,
  disabled = false,
  disabledReason = null,
  icon = null,
  fullWidth = false,
  additionalClasses = '',
  size = 'md',
  type = 'submit',
  color = 'rose-rush',
  variant = 'filled',
  _variantLightIndex = 6,
  variantDarkIndex = 8,
  tooltip = null,
  tooltipProps = {}, // Additional props for the Tooltip component
  ...props
}) => {
  const theme = useMantineTheme();

  const colors = theme.colors[color] || theme.colors.red;
  const primaryColor = colors[5];
  const darkColor = colors[variantDarkIndex] || colors[8];
  const lightestColor = colors[0];

  // Custom font size mapping based on size prop
  const fontSizes = {
    xs: '0.8rem',
    sm: '0.85rem',
    md: '0.9rem',
    lg: '1.0rem',
    xl: '1.1rem',
  };
  const customFontSize = fontSizes[size] || fontSizes.md;

  // Define styles dynamically using emotion css
  const rootClass = css`
    cursor: default;
    font-weight: 800;
    font-family: 'Cabinet Grotesk', sans-serif;
    transition:
      transform 0.15s cubic-bezier(0.4, 0, 0.2, 1),
      filter 0.15s cubic-bezier(0.4, 0, 0.2, 1),
      background-color 0.15s cubic-bezier(0.4, 0, 0.2, 1),
      color 0.15s cubic-bezier(0.4, 0, 0.2, 1),
      border-color 0.15s cubic-bezier(0.4, 0, 0.2, 1);

    border: none;

    ${variant === 'default' &&
    `
      color: #000;
      box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
      border: 1px solid #000;
      border-bottom: 5px solid #000;
      &:hover {
        background-color: #ebebeb;
      }
      &:active {
        transform: scale(0.95);
      }
      &:disabled {
        opacity: 0.5;
        cursor: not-allowed !important;
        transform: none;
      }
    `}

    ${variant === 'filled' &&
    `
      background-color: ${primaryColor};
      color: ${theme.white};
      box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
      border-bottom: 5px solid ${darkColor};
      &:hover {
        background-color: ${colors[4]};
        border-bottom: 5px solid ${colors[7]};
      }
      &:active {
        transform: scale(0.95);
      }
      &:disabled {
        opacity: 0.5;
        cursor: not-allowed !important;
        transform: none;
      }
    `}

    ${variant === 'light' &&
    `
      background-color: ${colors[1]};
      border-bottom: 5px solid ${colors[3]};
      color: ${color[7]};
      &:hover {
        background-color: ${colors[2]};
        color: ${colors[8]};
      }
      &:active {
        transform: scale(0.95);
      }
      &:disabled {
        opacity: 0.5;
        cursor: not-allowed !important;
        transform: none;
      }
    `}

    ${variant === 'outline' &&
    `
      background-color: transparent;
      border: 2px solid ${primaryColor};
      border-bottom: 5px solid ${primaryColor};
      color: ${primaryColor};
      &:hover {
        background-color: ${colors[0]};
        color: ${primaryColor};
      }
      &:active {
        transform: scale(0.95);
      }
      &:disabled {
        opacity: 0.5;
        cursor: not-allowed !important;
        transform: none;
      }
    `}

    ${(variant === 'subtle' || variant === 'unstyled') &&
    `
      background-color: transparent;
      color: ${primaryColor};
      border: none;
      border-bottom: 5px solid transparent;
      &:hover {
        background-color: ${lightestColor};
        border-bottom: 5px solid ${colors[3]};
      }
      &:active {
        transform: scale(0.95);
      }
      &:disabled {
        opacity: 0.5;
        cursor: not-allowed !important;
      }
    `}
  `;

  const buttonContent = (
    <MantineButton
      onClick={onClick}
      disabled={disabled}
      fullWidth={fullWidth}
      size={size}
      type={type}
      variant={variant}
      color={color}
      className={`${rootClass} ${additionalClasses}`}
      {...props}
    >
      <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: customFontSize }}>
        {icon}
        {children}
      </span>
    </MantineButton>
  );

  // Show disabledReason tooltip if button is disabled and has a reason
  const tooltipToShow = disabled && disabledReason ? disabledReason : tooltip;

  if (tooltipToShow) {
    return (
      <Tooltip label={tooltipToShow} {...tooltipProps}>
        {buttonContent}
      </Tooltip>
    );
  }

  return buttonContent;
};

export default Button;
